import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Group,
  List,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import segmentedControl from './SegmentedControl.module.css';

export function Upgrade({ user }) {
  const [billingFrequency, setBillingFrequency] = useState('annual');
  const priceLookupKey = `pro_${billingFrequency === 'annual' ? 'yearly' : 'monthly'}`;

  const handleUpgrade = async () => {
    window.open(
      `${window.location.origin}/checkout?price=${encodeURIComponent(priceLookupKey)}&email=${encodeURIComponent(user.email || '')}`,
      'popupWindow',
      'width=600,height=800,resizable,scrollbars'
    );
  };

  return (
    <Container size="lg" pos="relative" px="lg" py="xl">
      <Title order={1} fz={{ sm: rem(30), md: rem(45) }} fw={900} ta="center" mb="xl">
        Get access to all web slides features
      </Title>

      <Stack align="center" justify="center" gap="md" mt="md" mb="lg">
        <SegmentedControl
          value={billingFrequency}
          radius="xl"
          size="md"
          data={[
            { label: 'Annual billing', value: 'annual' },
            { label: 'Monthly billing', value: 'monthly' },
          ]}
          classNames={segmentedControl}
          onChange={setBillingFrequency}
          mb="md"
        />
      </Stack>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
        <Card shadow="md" radius="md" p="xl">
          <Box>
            <Title mb="md" fz={rem(50)} fw="800">
              <Text variant="gradient" gradient={{ from: '#FE9920', to: '#FF2A77' }} inherit>
                Free
              </Text>
            </Title>
            <Group align="baseline" gap="0" mb="md">
              <Title fw="700" size={rem(50)} mx="3px">
                $0
              </Title>
              {billingFrequency === 'annual' ? (
                <Text c="gray"> / year</Text>
              ) : (
                <Text c="gray"> / month</Text>
              )}
            </Group>

            <Text mb="md" fw="800" fz={rem(17)}>
              What's included:
            </Text>
            <List spacing="md" mb="xl" center icon={<IconCheck color="green" size={16} />}>
              <List.Item>
                <Text fz={rem(17)}>50 slide credits when generating Google Slides.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Web Slides presentations stored for up to 24 hours.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>
                  Create presentations via Google Docs, Notion, rich text editor, DOCX file import
                  or Markdown.
                </Text>
              </List.Item>
            </List>
          </Box>

          <Stack justify="flex-end" h="100%">
            <Button
              size="lg"
              bg="#FF2A77"
              component="a"
              href="https://workspace.google.com/marketplace/app/slides_generator_convert_docs%E1%B5%80%E1%B4%B9_to_slide/398255519813"
            >
              Get Started
            </Button>
          </Stack>
        </Card>
        <Card shadow="md" radius="md" p="xl">
          <Box>
            <Title mb="md" fz={rem(50)} fw="800">
              <Text variant="gradient" gradient={{ from: '#FE9920', to: '#FF2A77' }} inherit>
                Plus
              </Text>
            </Title>
            <Group align="baseline" gap="0" mb="md">
              {billingFrequency === 'annual' ? (
                <Title fw="700" size={rem(50)} mx="3px">
                  $60
                </Title>
              ) : (
                <Title fw="700" size={rem(50)} mx="3px">
                  $5
                </Title>
              )}
              {billingFrequency === 'annual' ? (
                <Text c="gray"> / year</Text>
              ) : (
                <Text c="gray"> / month</Text>
              )}
            </Group>

            <Text mb="md" fw="800" fz={rem(17)}>
              What's included:
            </Text>
            <List spacing="md" mb="xl" center icon={<IconCheck color="green" size={16} />}>
              <List.Item>
                <Text fz={rem(17)}>
                  {billingFrequency === 'annual'
                    ? '1200 slide credits per year'
                    : '100 slide credits per month'}{' '}
                  when generating Google Slides.
                </Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Cancel anytime.</Text>
              </List.Item>
            </List>
          </Box>

          <Stack justify="flex-end" h="100%">
            <Button
              id="plus"
              size="lg"
              bg="#FF2A77"
              component="a"
              href={
                billingFrequency === 'annual'
                  ? 'checkout?price=plus_yearly'
                  : 'checkout?price=plus_monthly'
              }
            >
              Upgrade
            </Button>
          </Stack>
        </Card>

        <Card shadow="md" radius="md" p="xl">
          <Box>
            <Title mb="md" fz={rem(50)} fw="800">
              <Text variant="gradient" gradient={{ from: '#FE9920', to: '#FF2A77' }} inherit>
                Pro
              </Text>
            </Title>
            <Group align="baseline" gap="0" mb="md">
              <Title fw="700" size={rem(50)} mx="3px">
                {billingFrequency === 'annual' ? (
                  <>
                    <Text td="line-through" component="span" inherit mr="md">
                      $120
                    </Text>
                    <Text component="span" inherit>
                      $80
                    </Text>
                  </>
                ) : (
                  <>
                    <Text td="line-through" component="span" inherit mr="md">
                      $15
                    </Text>
                    <Text component="span" inherit>
                      $8
                    </Text>
                  </>
                )}
              </Title>
              {billingFrequency === 'annual' ? (
                <Text c="gray"> / year</Text>
              ) : (
                <Text c="gray"> / month</Text>
              )}
              {billingFrequency === 'monthly' && (
                <Text c="gray">Discount applicable to first 3 months</Text>
              )}
            </Group>

            <Text mb="md" fw="800" fz={rem(17)}>
              What's included:
            </Text>
            <List spacing="md" mb="xl" center icon={<IconCheck color="green" size={16} />}>
              <List.Item>
                <Text fz={rem(17)}>Unlimited slide credits when generating Google Slides.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>
                  Remove Slides Generator branding from presentations and replace with your own.
                </Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Web Slides presentations stored indefinitely.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Cancel anytime.</Text>
              </List.Item>
            </List>
          </Box>

          <Stack justify="flex-end" h="100%">
            <Button
              id="pro"
              size="lg"
              bg="#FF2A77"
              component="a"
              href={
                billingFrequency === 'annual'
                  ? 'checkout?price=pro_yearly'
                  : 'checkout?price=pro_monthly'
              }
            >
              Upgrade
            </Button>
          </Stack>
        </Card>

        <Card shadow="md" radius="md" p="xl">
          <Box>
            <Title mb="md" fz={rem(50)} fw="800">
              <Text variant="gradient" gradient={{ from: '#FE9920', to: '#FF2A77' }} inherit>
                Team
              </Text>
            </Title>
            <Group align="baseline" gap="0" mb="md">
              <Title fw="700" size={rem(50)} mx="3px">
                {billingFrequency === 'annual' ? '$240' : '$30'}
              </Title>
              {billingFrequency === 'annual' ? (
                <Text c="gray"> / year</Text>
              ) : (
                <Text c="gray"> / month</Text>
              )}
            </Group>
            <Text mb="md" fw="800" fz={rem(17)}>
              What's included:
            </Text>
            <List spacing="md" mb="xl" center icon={<IconCheck color="green" size={16} />}>
              <List.Item>
                <Text fz={rem(17)}>
                  All features of <b>Pro</b> for up to 10 team members.
                </Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Dedicated onboarding.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Priority support.</Text>
              </List.Item>
              <List.Item>
                <Text fz={rem(17)}>Cancel anytime.</Text>
              </List.Item>
            </List>
          </Box>

          <Stack justify="flex-end" h="100%">
            <Button
              id="team"
              size="lg"
              bg="#FF2A77"
              component="a"
              href={
                billingFrequency === 'annual'
                  ? 'checkout?price=team_yearly'
                  : 'checkout?price=team_monthly'
              }
            >
              Upgrade
            </Button>
          </Stack>
        </Card>
      </SimpleGrid>
    </Container>
  );
}
